import classNames from 'classnames';
import DOMPurify from 'isomorphic-dompurify';
import useStyles from 'isomorphic-style-loader/useStyles';
import md from 'markdown-it';
import { Fragment } from 'react';
import type { AccordionTitleProps } from 'semantic-ui-react';
import {
  Accordion,
  Button,
  Container,
  Divider,
  Grid,
  Header,
  Icon,
  Label,
  Menu,
  Popup,
} from 'semantic-ui-react';

import { setTourProps } from '@store/actions/action-tour';
import {
  setWhatsNewDialog,
  setWhatsNewFeatureIndex,
  setWhatsNewPrevIndex,
  setWhatsNewShowCtrl,
} from '@store/actions/action-whatsnew';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import type { TourState } from '@store/reducers/reducer-tour';

import { useAppContext } from '../../app-context';

import s from './whats-new.scss';

interface WhatsNewUpdate {
  feature: string;
  updates: {
    title: string;
    description: string;
  }[];
  tour: TourState;
}

export type WhatsNewData = {
  version: string;
  summary: string;
  isWhatsNewCtrlShown?: boolean;
  updates: WhatsNewUpdate[];
  previous: Omit<WhatsNewData, 'isWhatsNewCtrlShown'>[];
};

interface WhatsNewProps {
  data: WhatsNewData;
}

// Construct the Markdown renderer
const mdObj = md({ breaks: true });

// Main view component
export const WhatsNew = (props: WhatsNewProps) => {
  useStyles(s);

  const { data } = props;
  const app = useAppSelector(state => state.app);
  const { uiVersion } = useAppContext();
  const dispatch = useAppDispatch();

  const { whatsNew, tour } = app;
  const { isWhatsNewModalOpen, featureIdx, prevIdx, isWhatsNewCtrlShown } =
    whatsNew;

  let el = <></>;

  if (data && isWhatsNewCtrlShown) {
    const fn = (e: unknown, titleProps: AccordionTitleProps) => {
      const { index } = titleProps;
      const newIndex = featureIdx === index ? -1 : index;
      if (typeof newIndex === 'number') {
        dispatch(setWhatsNewFeatureIndex(newIndex));
      }
    };

    let currData = data;

    if (data && prevIdx !== -1) {
      currData = data.previous[prevIdx];
    }

    el = (
      <div className={s.whatsNewWrapper}>
        <Container className={classNames(s.whatsNewContainer, s.first)}>
          <Header
            as="h4"
            style={{
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {prevIdx === -1 ? 'Welcome to ' : ''}Anchore Enterprise Client
              Version {currData.version || uiVersion}
              {prevIdx !== -1 ? (
                <Label basic size="tiny" color="blue">
                  Previous Release
                </Label>
              ) : null}
            </span>
            <Button
              size="tiny"
              compact
              color="violet"
              className={s.whatsNewDismissBtn}
              onClick={() => {
                dispatch(setWhatsNewShowCtrl());
              }}
            >
              <Icon name="thumbs up" />
              Got it!
            </Button>
          </Header>
        </Container>
        <Container className={s.whatsNewContainer}>
          <p>{currData.summary}</p>
        </Container>
        <Accordion className={s.whatsNewAccordion} as={Menu} fluid vertical>
          {currData?.updates?.map((item, idx) => (
            <Menu.Item
              key={item.feature}
              className={classNames(
                s.whatsNewAccordionItm,
                featureIdx === idx ? s.open : s.closed,
              )}
            >
              <Accordion.Title
                index={idx}
                active={featureIdx === idx}
                onClick={fn}
              >
                <Header as="h4" className={s.whatsNewAccordionItmHeader}>
                  <Icon
                    color="teal"
                    className={s.whatsNewAccordionItmHeaderIcon}
                    name={`chevron ${featureIdx === idx ? 'down' : 'right'}`}
                  />
                  {item.feature}
                </Header>
              </Accordion.Title>
              <Accordion.Content
                className={s.whatsNewAccordionItmContent}
                active={featureIdx === idx}
              >
                {item?.updates?.map((itm, idx2, list) => (
                  <Fragment key={itm.title}>
                    <Header
                      as="h5"
                      className={s.whatsNewAccordionItmContentHeader}
                    >
                      {itm.title}
                    </Header>
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: mdObj.render(
                          DOMPurify.sanitize(itm.description),
                        ),
                      }}
                      className={s.whatsNewAccordionItmContent}
                    />
                    {list.length - 1 !== idx2 || item.tour ? (
                      <Divider className={s.divider} fitted />
                    ) : null}
                  </Fragment>
                ))}
              </Accordion.Content>
            </Menu.Item>
          ))}
        </Accordion>
        {data.previous instanceof Array && data.previous.length ? (
          <Container className={s.whatsNewPrevContainer}>
            <Grid columns={2}>
              <Grid.Row>
                <Grid.Column width={8}>
                  {data.previous[prevIdx + 1] ? (
                    <Button
                      compact
                      size="mini"
                      onClick={() => {
                        dispatch(setWhatsNewPrevIndex(prevIdx + 1));
                      }}
                    >
                      <Icon name="angle double left" />
                      Version {data.previous[prevIdx + 1].version}
                    </Button>
                  ) : null}
                </Grid.Column>
                <Grid.Column width={8} textAlign="right">
                  {data.previous[prevIdx - 1] ? (
                    <Button
                      compact
                      size="mini"
                      onClick={() => {
                        dispatch(setWhatsNewPrevIndex(prevIdx - 1));
                      }}
                    >
                      Version {data.previous[prevIdx - 1].version}
                      <Icon name="angle double right" />
                    </Button>
                  ) : null}
                  {!prevIdx ? (
                    <Button
                      compact
                      floated="right"
                      size="mini"
                      onClick={() => {
                        dispatch(setWhatsNewPrevIndex(-1));
                      }}
                    >
                      Version {data.version}
                      <Icon name="angle double right" />
                    </Button>
                  ) : prevIdx === -1 &&
                    tour.tourProps &&
                    tour.tourProps?.['/*'] ? (
                    <Button
                      compact
                      basic
                      color="blue"
                      size="tiny"
                      floated="right"
                      onClick={() => {
                        dispatch(
                          setTourProps({
                            ...tour.tourProps,
                            '/*': {
                              isDisabled: false,
                              showIntro: true,
                            },
                          }),
                        );
                      }}
                    >
                      Take the New Feature Tour
                    </Button>
                  ) : null}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Container>
        ) : null}
      </div>
    );

    el = (
      <Menu.Item className={s.root}>
        <Popup
          className={classNames(
            s.whatsNewPopup,
            'animate__animated animate__fadeIn',
          )}
          basic
          on="click"
          open={isWhatsNewModalOpen}
          onOpen={() => {
            dispatch(setWhatsNewDialog(true));
          }}
          onClose={() => {
            dispatch(setWhatsNewDialog(false));
          }}
          content={el}
          position="bottom left"
          trigger={
            <div>
              <Button
                className={s.whatsNewBtn}
                color="green"
                size="mini"
                icon
                labelPosition="right"
              >
                <Icon name={isWhatsNewModalOpen ? 'caret up' : 'caret down'} />
                <span className={s.whatsNewBtnTxt}>What&#39;s New?</span>
              </Button>
              <div className={s.happyDog} />
            </div>
          }
        />
      </Menu.Item>
    );
  }

  return el;
};
